import React, { useState, useEffect } from 'react';
import { Search, Eye, Edit, Trash2, CheckCircle, XCircle, Printer, FileText, CreditCard, DollarSign } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { generateBookingReceipt } from '@/utils/bookingReceiptGenerator';
import { toast } from 'sonner';
import PaymentBreakdown from '@/components/PaymentBreakdown';
import Pagination from '@/components/Pagination';

const BookingList = () => {
  const [bookings, setBookings] = useState([]);
  const [courses, setCourses] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [collegeInfo, setCollegeInfo] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalItems, setTotalItems] = useState(0);
  
  // Payment Modal State
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [paymentData, setPaymentData] = useState({
    amount: 0,
    payment_modes: [],
    remarks: ''
  });
  const [paymentLoading, setPaymentLoading] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');
  const navigate = useNavigate();

  useEffect(() => {
    fetchBookings();
    fetchCourses();
    fetchConsultants();
    loadCollegeInfo();
  }, [statusFilter, currentPage, itemsPerPage]);

  const loadCollegeInfo = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/tenants/my-college/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setCollegeInfo({
          ...response.data,
          college_name: response.data.name || response.data.college_name,
          logo_url: response.data.logo || response.data.logo_url
        });
      }
    } catch (error) {
      console.error('Error fetching college info:', error);
    }
  };

  const fetchBookings = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/seat-booking/?page=${currentPage}&limit=${itemsPerPage}`;
      if (statusFilter !== 'all') {
        url += `&status=${statusFilter}`;
      }
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Handle both old (array) and new (paginated object) response formats
      if (Array.isArray(response.data)) {
        setBookings(response.data);
        setTotalItems(response.pagination?.total || response.data.length);
      } else {
        setBookings(response.data?.data || []);
        setTotalItems(response.data?.total || 0);
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  // Reset to page 1 when filters change
  const handleStatusFilterChange = (newStatus) => {
    setCurrentPage(1);
    setStatusFilter(newStatus);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchConsultants = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/consultants/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConsultants(response.data);
    } catch (error) {
      console.error('Error fetching consultants:', error);
    }
  };

  const handleConvertToAdmission = (booking) => {
    // Navigate to unified admission form with booking data
    // The form will auto-detect entry type from booking
    navigate(`/students/admission?booking=${booking.id}`);
  };

  const handleEditBooking = (booking) => {
    // Navigate to edit page with booking data
    navigate(`/seat-booking/book?edit=${booking.id}`, { state: { booking } });
  };

  const handleCancelBooking = async (bookingId, studentName) => {
    if (window.confirm(`Are you sure you want to delete this booking for ${studentName}?`)) {
      try {
        await axios.delete(`${API_URL}/api/seat-booking/${bookingId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Booking deleted successfully!');
        fetchBookings();
      } catch (error) {
        console.error('Error deleting booking:', error);
        alert('Failed to delete booking. Please try again.');
      }
    }
  };

  // Open Payment Modal for Payment Pending entries
  const handleCollectPayment = (booking) => {
    setSelectedBooking(booking);
    setPaymentData({
      amount: booking.booking_amount || 0,
      payment_modes: [],
      remarks: ''
    });
    setShowPaymentModal(true);
  };

  // Submit Payment
  const handleSubmitPayment = async () => {
    if (!selectedBooking) return;
    
    // Validate payment modes
    if (!paymentData.payment_modes || paymentData.payment_modes.length === 0) {
      toast.error('Please add at least one payment mode');
      return;
    }

    const totalPaid = paymentData.payment_modes.reduce((sum, mode) => sum + parseFloat(mode.amount || 0), 0);
    if (Math.abs(totalPaid - paymentData.amount) > 0.01) {
      toast.error(`Payment total (₹${totalPaid.toLocaleString('en-IN')}) must equal booking amount (₹${paymentData.amount.toLocaleString('en-IN')})`);
      return;
    }

    setPaymentLoading(true);
    try {
      // Generate receipt number
      const receiptNo = `SB${Date.now().toString().slice(-8)}`;
      
      // Update seat booking with payment details
      await axios.put(`${API_URL}/api/seat-booking/${selectedBooking.id}`, {
        status: 'Booked',
        student_status: 'form_submitted',  // Ready for conversion to admission
        payment_modes: paymentData.payment_modes,
        amount: paymentData.amount,
        booking_amount: paymentData.amount,
        payment_remarks: paymentData.remarks,
        payment_date: new Date().toISOString().split('T')[0],
        receipt_no: receiptNo
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Payment collected successfully!');
      setShowPaymentModal(false);
      setSelectedBooking(null);
      fetchBookings(); // Refresh list
    } catch (error) {
      console.error('Error collecting payment:', error);
      toast.error('Failed to collect payment. Please try again.');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handlePrintReceipt = async (booking) => {
    try {
      console.log('=== RECEIPT GENERATION STARTED ===');
      console.log('Booking ID:', booking.booking_id);
      console.log('Course ID:', booking.course_id);
      console.log('Entry Type:', booking.entry_type, '| Is Lateral:', booking.is_lateral_entry);
      
      // Fetch course fee from fee structures
      let courseFee = 0;
      try {
        const feeResponse = await axios.get(`${API_URL}/api/master-settings/fee-structures`, {
          params: { course_id: booking.course_id },
          headers: { Authorization: `Bearer ${token}` }
        });
        
        console.log(`✓ Fee structures API returned ${feeResponse.data?.length || 0} records`);
        const feeData = Array.isArray(feeResponse.data) ? feeResponse.data : (feeResponse.data?.data || []);
        
        if (feeData && feeData.length > 0) {
          // Filter by entry type
          const isLateral = booking.is_lateral_entry || booking.entry_type === 'Lateral';
          console.log(`Filtering for: ${isLateral ? 'Lateral (6 semesters)' : 'Regular (8 semesters)'}`);
          
          let matchingFees = feeData.filter(fee => {
            if (isLateral) {
              return fee.is_lateral_entry === true || fee.total_semesters === 6;
            } else {
              return fee.is_lateral_entry === false || fee.total_semesters === 8;
            }
          });
          if (matchingFees.length === 0) matchingFees = feeData;
          
          console.log(`✓ Found ${matchingFees.length} matching fee structures`);
          
          // Try to match by session_id first
          if (booking.session_id) {
            const sessionMatch = matchingFees.find(f => f.session_id === booking.session_id);
            if (sessionMatch) {
              courseFee = sessionMatch.total_fee || sessionMatch.course_fee || 0;
              console.log(`✅ COURSE FEE (session match): ₹${courseFee}`);
            }
          }
          
          // Fallback if no session match
          if (courseFee === 0 && matchingFees.length > 0) {
            courseFee = matchingFees[0].total_fee || matchingFees[0].course_fee || 0;
            console.log(`⚠️ Using fallback fee: ₹${courseFee}`);
          }
        } else {
          console.error('❌ No fee structures found for this course!');
        }
      } catch (feeError) {
        console.error('❌ Error fetching course fee:', feeError);
      }
      
      // Enrich booking data
      const enrichedBooking = {
        ...booking,
        course_name: getCourseName(booking.course_id),
        consultant_name: getConsultantName(booking.consultant_id),
        student_name: booking.student_name || `${booking.first_name || ''} ${booking.middle_name || ''} ${booking.last_name || ''}`.trim(),
        course_fee: parseFloat(booking.course_fee) > 0 ? parseFloat(booking.course_fee) : courseFee,
        total_course_fee: parseFloat(booking.course_fee) > 0 ? parseFloat(booking.course_fee) : courseFee
      };
      
      console.log('📄 Generating PDF with course fee:', courseFee);
      console.log('=== RECEIPT GENERATION COMPLETE ===');
      
      // Generate PDF receipt with correct tenant's college info
      await generateBookingReceipt(enrichedBooking, collegeInfo);
    } catch (error) {
      console.error('❌ Error generating receipt:', error);
      alert('Failed to generate receipt. Please try again.');
    }
  };

  const getCourseName = (courseId) => {
    const course = courses.find(c => c.id === courseId);
    return course ? course.name : 'N/A';
  };

  const getConsultantName = (consultantId) => {
    const consultant = consultants.find(c => c.id === consultantId);
    return consultant ? consultant.name : 'Direct';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Booked': return 'bg-blue-100 text-blue-800';
      case 'form_submitted': return 'bg-yellow-100 text-yellow-800';
      case 'Form Submitted': return 'bg-yellow-100 text-yellow-800';
      case 'Converted': return 'bg-green-100 text-green-800';
      case 'Cancelled': return 'bg-red-100 text-red-800';
      case 'Payment Pending': return 'bg-orange-100 text-orange-800';
      case 'payment_pending': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'form_submitted':
      case 'Form Submitted': return <FileText className="h-4 w-4" />;
      case 'Converted': return <CheckCircle className="h-4 w-4" />;
      case 'Cancelled': return <XCircle className="h-4 w-4" />;
      case 'Payment Pending':
      case 'payment_pending': return <DollarSign className="h-4 w-4" />;
      default: return null;
    }
  };
  
  const getStatusLabel = (status) => {
    switch (status) {
      case 'form_submitted': return 'Form Submitted';
      case 'Booked': return 'Booked';
      case 'Converted': return 'Admitted';
      case 'Payment Pending': return 'Payment Pending';
      case 'payment_pending': return 'Payment Pending';
      default: return status;
    }
  };

  const filteredBookings = bookings.filter(booking => {
    const fullName = booking.student_name || `${booking.first_name || ''} ${booking.middle_name || ''} ${booking.last_name || ''}`.trim();
    return (
      fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (booking.email && booking.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (booking.phone && booking.phone.includes(searchTerm))
    );
  });

  const totalAmount = filteredBookings.reduce((sum, booking) => sum + (booking.booking_amount || booking.amount || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Seat Bookings</h1>
          <p className="text-sm text-gray-500 mt-1">Manage all seat bookings and reservations</p>
        </div>
        <button
          onClick={() => navigate('/seat-booking/book')}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Book New Seat
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-sm text-gray-500">Total Bookings</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{filteredBookings.length}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-sm text-gray-500">Total Amount</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">₹{totalAmount.toLocaleString('en-IN')}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-sm text-gray-500">Converted</p>
          <p className="text-2xl font-bold text-green-600 mt-1">
            {filteredBookings.filter(b => b.status === 'Converted').length}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-sm text-gray-500">Pending</p>
          <p className="text-2xl font-bold text-blue-600 mt-1">
            {filteredBookings.filter(b => b.status === 'Booked').length}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, email, or phone..."
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={statusFilter}
              onChange={(e) => handleStatusFilterChange(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="Payment Pending">Payment Pending</option>
              <option value="Booked">Booked</option>
              <option value="Converted">Converted</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Booking ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Receipt No</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student Details</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Course</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entry Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Session</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Mode</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Booking Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Consultant</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="9" className="px-6 py-8 text-center text-gray-500">Loading...</td>
                </tr>
              ) : filteredBookings.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-6 py-8 text-center text-gray-500">No bookings found</td>
                </tr>
              ) : (
                filteredBookings.map((booking) => (
                  <tr key={booking.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-bold text-blue-600">{booking.booking_id || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-bold text-purple-600">{booking.receipt_no || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {booking.student_name || `${booking.first_name || ''} ${booking.middle_name || ''} ${booking.last_name || ''}`.trim()}
                        </div>
                        <div className="text-sm text-gray-500">{booking.email}</div>
                        <div className="text-sm text-gray-500">📞 {booking.phone}</div>
                        {booking.whatsapp_no && booking.whatsapp_no !== booking.phone && (
                          <div className="text-sm text-green-600">💬 {booking.whatsapp_no}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{getCourseName(booking.course_id)}</td>
                    <td className="px-6 py-4">
                      {booking.is_lateral_entry ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                          Lateral Entry (Sem {booking.entry_semester})
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Regular
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{booking.session || 'N/A'}</td>
                    <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                      ₹{(booking.booking_amount || booking.amount || 0).toLocaleString('en-IN')}
                      {booking.is_custom_fee && (
                        <span data-testid={`custom-fee-badge-${booking.id}`} className="block mt-1 text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-semibold w-fit">Custom fee</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {booking.payment_modes && booking.payment_modes.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {booking.payment_modes.map((pm, idx) => (
                            <div key={idx} className="inline-flex flex-col items-start px-2 py-1 rounded-md text-xs bg-blue-100 text-blue-800">
                              <span className="font-medium">{pm.mode}: ₹{parseFloat(pm.amount).toLocaleString('en-IN')}</span>
                              {pm.transaction_number && (
                                <span className="text-xs text-blue-600">Ref: {pm.transaction_number}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800">
                          {booking.payment_mode || 'N/A'}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{booking.booking_date}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{getConsultantName(booking.consultant_id)}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(booking.status === 'Converted' ? 'Converted' : (booking.student_status || booking.status))}`}>
                        {getStatusIcon(booking.status === 'Converted' ? 'Converted' : (booking.student_status || booking.status))}
                        {getStatusLabel(booking.status === 'Converted' ? 'Converted' : (booking.student_status || booking.status))}
                      </span>
                      {/* Only show "Ready to Convert" if NOT already converted */}
                      {booking.student_status === 'form_submitted' && booking.status !== 'Converted' && (
                        <p className="text-xs text-yellow-600 mt-1">Ready to Convert</p>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        {/* Print Receipt - Available for all bookings */}
                        <button
                          onClick={() => handlePrintReceipt(booking)}
                          className="text-purple-600 hover:text-purple-900"
                          title="Print Receipt"
                        >
                          <Printer className="h-4 w-4" />
                        </button>
                        
                        {/* Collect Payment - For Payment Pending status */}
                        {(booking.status === 'Payment Pending' || booking.student_status === 'payment_pending') && (
                          <button
                            onClick={() => handleCollectPayment(booking)}
                            className="text-orange-600 hover:text-orange-900 bg-orange-50 p-1.5 rounded"
                            title="Collect Payment"
                          >
                            <CreditCard className="h-4 w-4" />
                          </button>
                        )}
                        
                        {/* Convert button - Only show for Booked status that is NOT already Converted */}
                        {booking.status === 'Booked' && !booking.converted_to_admission && (
                          <>
                            <button
                              onClick={() => handleConvertToAdmission(booking)}
                              className="text-green-600 hover:text-green-900"
                              title={booking.is_lateral_entry ? "Convert to Lateral Admission" : "Convert to Regular Entry Admission"}
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleEditBooking(booking)}
                              className="text-blue-600 hover:text-blue-900"
                              title="Edit Seat Booking"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleCancelBooking(booking.id, booking.student_name || `${booking.first_name || ''} ${booking.middle_name || ''} ${booking.last_name || ''}`.trim())}
                              className="text-red-600 hover:text-red-900"
                              title="Delete Seat Booking"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </>
                        )}
                        
                        {/* Show admission number if converted */}
                        {booking.status === 'Converted' && booking.admission_number && (
                          <span className="text-xs text-green-600 font-medium">
                            {booking.admission_number}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={Math.ceil(totalItems / itemsPerPage)}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      </div>

      {/* Payment Collection Modal */}
      {showPaymentModal && selectedBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                💳 Collect Payment
              </h3>
              
              {/* Student Info */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg mb-4 border border-blue-200">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-base font-semibold text-gray-900">
                      {selectedBooking.student_name || `${selectedBooking.first_name || ''} ${selectedBooking.last_name || ''}`.trim()}
                    </p>
                    <p className="text-sm text-gray-600">{selectedBooking.email}</p>
                    <p className="text-sm text-gray-600">📞 {selectedBooking.phone}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Booking ID</p>
                    <p className="text-sm font-bold text-blue-600">{selectedBooking.booking_id}</p>
                  </div>
                </div>
                {selectedBooking.online_application_number && (
                  <div className="mt-2 pt-2 border-t border-blue-200">
                    <p className="text-xs text-blue-700">
                      🌐 Online Application No: <span className="font-semibold">{selectedBooking.online_application_number}</span>
                    </p>
                  </div>
                )}
              </div>

              {/* Course & Amount Info */}
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500">Course</p>
                  <p className="text-sm font-semibold text-gray-900">{getCourseName(selectedBooking.course_id)}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500">Session</p>
                  <p className="text-sm font-semibold text-gray-900">{selectedBooking.session || 'N/A'}</p>
                </div>
                <div className="bg-green-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500">Booking Amount</p>
                  <p className="text-lg font-bold text-green-700">₹{(paymentData.amount || 0).toLocaleString('en-IN')}</p>
                </div>
              </div>

              {/* Payment Breakdown Component */}
              <div className="mb-4">
                <PaymentBreakdown
                  totalAmount={paymentData.amount}
                  paymentModes={paymentData.payment_modes}
                  onChange={(modes) => setPaymentData({ ...paymentData, payment_modes: modes })}
                  label="Payment Breakdown *"
                />
              </div>

              {/* Remarks */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Remarks (Optional)
                </label>
                <textarea
                  value={paymentData.remarks}
                  onChange={(e) => setPaymentData({...paymentData, remarks: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows={2}
                  placeholder="Any additional notes..."
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  onClick={() => {
                    setShowPaymentModal(false);
                    setSelectedBooking(null);
                  }}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={paymentLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitPayment}
                  disabled={paymentLoading || !paymentData.payment_modes || paymentData.payment_modes.length === 0}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {paymentLoading ? (
                    <>
                      <span className="animate-spin">⏳</span>
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="h-4 w-4" />
                      Collect Payment
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingList;